/**
 * Always-on badge for the catalog's health lint — the same check that
 * catalog_push runs (and alarms on) on every publish, surfaced here so an
 * operator sees a drifting catalog without having to go look at CloudWatch.
 */

import { useQuery } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { ChevronDown } from "lucide-react";

import { getCatalogHealth } from "@/api/components";
import { Icon, StatusPill } from "@/components/ui";

export function CatalogHealthBadge() {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const health = useQuery({
    queryKey: ["catalog", "health"],
    queryFn: () => getCatalogHealth(token),
    enabled: !!token,
    // The lint re-reads the whole catalog — no need to hit it on every
    // navigation between the two catalog pages.
    staleTime: 60_000,
  });

  if (health.isLoading || !health.data) return null;
  const { healthy, anomalyCount, findings } = health.data;
  const warnings = findings.filter((f) => f.severity === "warning").length;

  if (healthy && warnings === 0) {
    return <StatusPill tone="success">Catalog healthy</StatusPill>;
  }
  return (
    <details className="group inline-block">
      {/* The pill IS the summary's content: `list-none` and the WebKit marker rule
          drop the native triangle, which would sit inside the pill — so the pill
          carries its own chevron, the only sign that it opens a list. */}
      <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden">
        <StatusPill tone={healthy ? "warning" : "danger"}>
          {anomalyCount > 0
            ? `${anomalyCount} catalog issue${anomalyCount > 1 ? "s" : ""}`
            : `${warnings} warning${warnings > 1 ? "s" : ""}`}
          <Icon icon={ChevronDown} size="sm" className="-mr-0.5 transition-transform group-open:rotate-180" />
        </StatusPill>
      </summary>
      <ul className="mt-2 space-y-1 rounded border border-line bg-surface p-3 text-xs text-ink shadow-sm">
        {findings.map((f) => (
          <li key={`${f.code}:${f.subject}`}>
            <span className="font-mono">{f.subject}</span> — {f.message}
          </li>
        ))}
      </ul>
    </details>
  );
}
